// src/views/admin/ADInscriptionReinscription.jsx
//

// - getTypesEleve -> getTypesEleves (mauvais nom de fonction dans adminService,
//   la liste des types d'élèves ne se chargeait donc jamais).
// - Le champ "Type d'élève" est maintenant TOUJOURS affiché (plus caché
//   quand la liste est vide) et devient OBLIGATOIRE dès qu'une classe est
//   choisie, car c'est lui qui pilote le calcul des frais de scolarité.
// - Le total des frais s'affiche automatiquement dès que Classe + Type
//   d'élève sont renseignés (via getAffectationsFrais).
// - NOUVEAU : une fois l'inscription enregistrée, on redirige directement
//   vers la fiche de situation/versements de l'élève (module Caisse) au
//   lieu de la liste des élèves, pour permettre d'encaisser la scolarité
//   dans la foulée. Si l'inscriptionId n'a pas pu être résolu depuis la
//   réponse de l'API, on retombe sur l'ancien comportement (liste élèves).
import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAdminService } from "../../services/adminService";
import { Loader } from "../../components/Loader";
import { useToastHook } from "../../utils/useToast";
import { urlimage } from "../../utils/config";

const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  if (path.startsWith('/')) return `${urlimage}${path}`;
  return `${urlimage}/${path}`;
};

const aujourdISO = () => new Date().toISOString().slice(0, 10);

export default function ADInscriptionReinscription({ slug }) {
  const navigate = useNavigate();
  const { eleveId } = useParams();
  const toast = useToastHook();
  const adminService = useAdminService();
  const {
    getEleves, getEleveDetail, getClasses, getAnneesScolaires, createInscription,
    getAffectationsFrais,
  } = adminService;
  const getTypesEleveFn = adminService.getTypesEleves; // ← CORRIGÉ (était getTypesEleve)

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [classes, setClasses] = useState([]);
  const [annees, setAnnees] = useState([]);
  const [typesEleve, setTypesEleve] = useState([]);

  // ─── Sélection de l'élève (si non fourni par l'URL) ──────────────────────
  const [eleveSelectionne, setEleveSelectionne] = useState(null);
  const [rechercheEleve, setRechercheEleve] = useState("");
  const [resultatsEleves, setResultatsEleves] = useState([]);
  const [rechercheEnCours, setRechercheEnCours] = useState(false);

  // ─── Formulaire d'inscription ─────────────────────────────────────────────
  const [form, setForm] = useState({
    classe: "", annee_scolaire: "",
    is_nouveau: false, is_redoublant: false, classe_redoublee: "", type_eleve: "",
    date_inscription: aujourdISO(),
  });

  // ─── Total des frais de scolarité, calculé automatiquement ────────────────
  const [totalScolarite, setTotalScolarite] = useState(null);
  const [totalScolariteLoading, setTotalScolariteLoading] = useState(false);
  const [affectationsClasse, setAffectationsClasse] = useState([]); // détail par tranche

  // ─── CHARGEMENT INITIAL ───────────────────────────────────────────────────
  useEffect(() => {
    if (!slug) return;
    (async () => {
      setLoading(true);
      try {
        const [classesData, anneesData] = await Promise.all([
          getClasses(slug, { all_years: 'true', show_archived: 'true' }),
          getAnneesScolaires(slug),
        ]);
        setClasses(classesData || []);
        setAnnees(anneesData || []);
        const active = (anneesData || []).find(a => a.actif) || null;
        setForm(prev => ({ ...prev, annee_scolaire: String(active?.id || "") }));

        if (getTypesEleveFn) {
          try {
            const data = await getTypesEleveFn(slug);
            setTypesEleve(data?.results || data || []);
          } catch {
            setTypesEleve([]);
            toast.error("Impossible de charger les types d'élèves");
          }
        }

        if (eleveId) {
          const full = await getEleveDetail(slug, eleveId);
          setEleveSelectionne(full);
          setForm(prev => ({ ...prev, is_nouveau: !(full.inscriptions?.length > 0) }));
        }
      } catch {
        toast.error("Impossible de charger les données nécessaires à l'inscription");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, eleveId]);

  // ─── RECHERCHE D'ÉLÈVE (si aucun eleveId dans l'URL) ─────────────────────
  useEffect(() => {
    if (eleveId) return;
    const t = setTimeout(async () => {
      setRechercheEnCours(true);
      try {
        const data = await getEleves(slug, {
          page_size: 20,
          ...(rechercheEleve ? { search: rechercheEleve } : {}),
        });
        setResultatsEleves(data.results || data || []);
      } catch {
        // silencieux
      } finally {
        setRechercheEnCours(false);
      }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rechercheEleve, eleveId, slug]);

  // ─── TOTAL DES FRAIS DE SCOLARITÉ (auto, dès que classe + type élève) ─────
  useEffect(() => {
    if (!form.classe || !form.type_eleve || !getAffectationsFrais) {
      setTotalScolarite(null);
      setAffectationsClasse([]);
      return;
    }
    let annule = false;
    (async () => {
      setTotalScolariteLoading(true);
      try {
        const affectations = await getAffectationsFrais(slug, { classe: form.classe });
        if (annule) return;
        const filtrees = (affectations || []).filter(a => String(a.type_eleve) === String(form.type_eleve));
        const total = filtrees.reduce((somme, a) => somme + Number(a.montant || 0), 0);
        setTotalScolarite(total);
        setAffectationsClasse(filtrees);
      } catch {
        if (!annule) { setTotalScolarite(null); setAffectationsClasse([]); }
      } finally {
        if (!annule) setTotalScolariteLoading(false);
      }
    })();
    return () => { annule = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.classe, form.type_eleve]);

  const classesDisponibles = useMemo(
    () => classes.filter(c => String(c.annee_scolaire) === String(form.annee_scolaire) && !c.archived),
    [classes, form.annee_scolaire]
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      return new Date(dateStr).toLocaleDateString("fr-FR");
    } catch {
      return dateStr;
    }
  };

  const choisirEleve = (e) => {
    setEleveSelectionne(e);
    setForm(prev => ({ ...prev, is_nouveau: !(e.inscriptions?.length > 0) }));
    getEleveDetail(slug, e.id).then(setEleveSelectionne).catch(() => {});
  };

const changerEleve = () => {
  // Toujours réinitialiser l'état local d'abord, avant toute navigation,
  // sinon avec un eleveId dans l'URL le composant garde l'ancien élève
  // en mémoire jusqu'au clic suivant (bug du "double clic").
  setEleveSelectionne(null);
  setRechercheEleve("");
  setResultatsEleves([]);
  if (eleveId) {
    navigate(`/${slug}/admin/eleves/inscrire`);
  }
};

  const annuler = () => navigate(`/${slug}/admin/eleves`);

  const handleSubmit = async () => {
    if (!eleveSelectionne) {
      toast.error("Sélectionnez d'abord un élève");
      return;
    }
    if (!form.annee_scolaire) { toast.error("L'année scolaire est requise"); return; }
    if (!form.classe) { toast.error("La classe est requise"); return; }
    if (!form.date_inscription) { toast.error("La date d'inscription est requise"); return; }
    if (!form.type_eleve) { toast.error("Le type d'élève est requis (il détermine les frais de scolarité)"); return; }
    if (form.is_redoublant && !form.classe_redoublee) {
      toast.error("Précisez la classe redoublée");
      return;
    }

    setSaving(true);
    try {
      const data = {
        eleve: parseInt(eleveSelectionne.id),
        classe: parseInt(form.classe),
        annee_scolaire: parseInt(form.annee_scolaire),
        date_inscription: form.date_inscription,
        is_nouveau: !!form.is_nouveau,
        is_redoublant: !!form.is_redoublant,
        type_eleve: parseInt(form.type_eleve),
      };
      if (form.is_redoublant && form.classe_redoublee) {
        data.classe_redoublee = parseInt(form.classe_redoublee);
      }
      const response = await createInscription(slug, data);
      toast.success(response.message || "Inscription enregistrée avec succès");

      // Accès direct au paiement des frais de scolarité juste après
      // l'inscription, quand l'inscriptionId est disponible dans la réponse.
      //
      // ⚠️ La forme exacte de la réponse de createInscription n'a pas été
      // vérifiée contre le code de l'endpoint Django correspondant (non
      // fourni) — on couvre les formes les plus probables. À ajuster si
      // besoin une fois l'endpoint confirmé.
      const inscriptionId = response?.id ?? response?.inscription?.id ?? null;
      if (inscriptionId) {
        navigate(`/${slug}/admin/caisse?tab=situation&inscription=${inscriptionId}`);
      } else {
        navigate(`/${slug}/admin/eleves`);
      }
    } catch (error) {
      toast.error(
        error.response?.data?.error ||
        error.response?.data?.non_field_errors?.[0] ||
        error.response?.data?.type_eleve?.[0] ||
        "Erreur lors de l'inscription"
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="page-inscription">
      <div className="page-header">
        <button className="ibtn" onClick={annuler} title="Retour"><i className="ti ti-arrow-left" /></button>
        <h2>Fiche d'inscription</h2>
      </div>

      <div className="card" style={{ maxWidth: 780, margin: "0 auto", padding: 24 }}>

        {/* ─── ÉTAPE : CHOIX DE L'ÉLÈVE ─── */}
        {!eleveSelectionne ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div className="fg">
              <label>Rechercher l'élève à inscrire</label>
              <input
                type="text"
                placeholder="Nom, prénom ou matricule..."
                value={rechercheEleve}
                onChange={e => setRechercheEleve(e.target.value)}
                autoFocus
              />
            </div>
            <div style={{ maxHeight: 360, overflowY: "auto", display: "flex", flexDirection: "column", gap: 4 }}>
              {rechercheEnCours && <div style={{ fontSize: 13, color: "var(--h)" }}>Recherche...</div>}
              {!rechercheEnCours && resultatsEleves.length === 0 && (
                <div style={{ fontSize: 13, color: "var(--h)" }}>
                  {rechercheEleve ? "Aucun élève trouvé." : "Commencez à taper pour rechercher un élève."}
                </div>
              )}
              {resultatsEleves.map(e => (
                <div
                  key={e.id}
                  onClick={() => choisirEleve(e)}
                  style={{
                    display: "flex", alignItems: "center", gap: 12,
                    padding: "8px 10px", borderRadius: 6, cursor: "pointer", background: "var(--s2)",
                  }}
                >
                  {e.photo ? (
                    <img src={getImageUrl(e.photo)} alt="" style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover" }} />
                  ) : (
                    <div style={{
                      width: 32, height: 32, borderRadius: "50%", background: "var(--b)",
                      display: "flex", alignItems: "center", justifyContent: "center", color: "var(--h)"
                    }}>
                      <i className="ti ti-user" style={{ fontSize: 16 }} />
                    </div>
                  )}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{e.prenom} {e.nom}</div>
                    <div style={{ fontSize: 12, color: "var(--m)" }}>{e.matricule}</div>
                  </div>
                  {e.inscriptions?.length > 0 && (
                    <span className="pill">Déjà inscrit {e.inscriptions.length}x</span>
                  )}
                </div>
              ))}
            </div>
            <div className="factions">
              <button className="bsec" onClick={annuler}>Annuler</button>
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {/* Rappel de l'élève sélectionné */}
            <div style={{
              display: "flex", alignItems: "center", gap: 14,
              padding: "12px 14px", background: "var(--s2)", borderRadius: 8,
            }}>
              {eleveSelectionne.photo ? (
                <img
                  src={getImageUrl(eleveSelectionne.photo)}
                  alt=""
                  style={{ width: 56, height: 56, borderRadius: "50%", objectFit: "cover" }}
                />
              ) : (
                <div style={{
                  width: 56, height: 56, borderRadius: "50%", background: "var(--b)",
                  display: "flex", alignItems: "center", justifyContent: "center", color: "var(--h)", fontSize: 22,
                }}>
                  <i className="ti ti-user" />
                </div>
              )}
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 15 }}>{eleveSelectionne.prenom} {eleveSelectionne.nom}</div>
                <div style={{ fontSize: 12, color: "var(--m)" }}>Matricule : {eleveSelectionne.matricule}</div>
              </div>
              <button className="bsec" style={{ fontSize: 12, padding: "4px 10px" }} onClick={changerEleve}>
                Changer d'élève
              </button>
            </div>

            {/* Historique des inscriptions, utile pour décider "redoublant" */}
            {eleveSelectionne.inscriptions?.length > 0 && (
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--t)", marginBottom: 6 }}>
                  Historique d'inscriptions
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {eleveSelectionne.inscriptions.map((ins, idx) => (
                    <div key={idx} style={{
                      display: "flex", justifyContent: "space-between",
                      padding: "6px 10px", fontSize: 13,
                      background: idx % 2 === 0 ? "var(--s2)" : "transparent", borderRadius: 4,
                    }}>
                      <span>
                        {ins.classe_nom} ({ins.annee_scolaire_nom})
                        {ins.is_redoublant && <span style={{ color: "var(--h)" }}> · redouble {ins.classe_redoublee_nom || ""}</span>}
                      </span>
                      <span style={{ fontSize: 12, color: "var(--m)" }}>{formatDate(ins.date_inscription)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Formulaire d'inscription */}
            <div className="fgrid">
              <div className="fg">
                <label>Année scolaire <span style={{ color: "var(--rd)" }}>*</span></label>
                <select
                  value={form.annee_scolaire}
                  onChange={e => setForm(p => ({ ...p, annee_scolaire: e.target.value, classe: "" }))}
                >
                  <option value="">— Sélectionner —</option>
                  {annees.filter(a => !a.archived).map(a => (
                    <option key={a.id} value={a.id}>{a.nom} {a.actif ? "✓" : ""}</option>
                  ))}
                </select>
              </div>
              <div className="fg">
                <label>Classe <span style={{ color: "var(--rd)" }}>*</span></label>
                <select
                  value={form.classe}
                  onChange={e => setForm(p => ({ ...p, classe: e.target.value }))}
                  disabled={!form.annee_scolaire}
                >
                  <option value="">— Sélectionner —</option>
                  {classesDisponibles.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
                </select>
              </div>
            </div>

            <div className="fg">
              <label>Date d'inscription <span style={{ color: "var(--rd)" }}>*</span></label>
              <input
                type="date"
                value={form.date_inscription}
                onChange={e => setForm(p => ({ ...p, date_inscription: e.target.value }))}
              />
              <span style={{ fontSize: 11, color: "var(--h)" }}>Préremplie à aujourd'hui, modifiable si besoin.</span>
            </div>

            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
              <input
                type="checkbox"
                checked={form.is_nouveau}
                onChange={e => setForm(p => ({ ...p, is_nouveau: e.target.checked }))}
              />
              Nouvel élève dans l'établissement
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
              <input
                type="checkbox"
                checked={form.is_redoublant}
                onChange={e => setForm(p => ({ ...p, is_redoublant: e.target.checked, classe_redoublee: "" }))}
              />
              Redoublant
            </label>
            {form.is_redoublant && (
              <div className="fg">
                <label>Classe redoublée <span style={{ color: "var(--rd)" }}>*</span></label>
                <select
                  value={form.classe_redoublee}
                  onChange={e => setForm(p => ({ ...p, classe_redoublee: e.target.value }))}
                >
                  <option value="">— Sélectionner —</option>
                  {classes.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
                </select>
              </div>
            )}

            {/* ─── Type d'élève — TOUJOURS affiché et OBLIGATOIRE ─── */}
            <div className="fg">
              <label>Type d'élève <span style={{ color: "var(--rd)" }}>*</span></label>
              <select
                value={form.type_eleve}
                onChange={e => setForm(p => ({ ...p, type_eleve: e.target.value }))}
                disabled={typesEleve.length === 0}
              >
                <option value="">— Sélectionner —</option>
                {typesEleve.map(t => <option key={t.id} value={t.id}>{t.libelle}</option>)}
              </select>
              {typesEleve.length === 0 && (
                <span style={{ fontSize: 11, color: "var(--rd)" }}>
                  Aucun type d'élève configuré — allez dans Caisse → Configuration → Types d'élèves
                </span>
              )}
            </div>

            {/* ─── Total frais de scolarité — affiché automatiquement ─── */}
            {form.classe && form.type_eleve && (
              <div style={{
                padding: "10px 12px", borderRadius: 8, background: "var(--bl-bg)",
              }}>
                <div style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  fontWeight: 700, fontSize: 14,
                }}>
                  <span>Total frais de scolarité à payer</span>
                  <span>
                    {totalScolariteLoading
                      ? "Calcul..."
                      : totalScolarite !== null ? `${totalScolarite.toLocaleString()} FCFA` : "—"}
                  </span>
                </div>
                {!totalScolariteLoading && affectationsClasse.length === 0 && (
                  <div style={{ fontSize: 12, color: "var(--rd)", marginTop: 4 }}>
                    Aucun frais affecté pour cette classe et ce type d'élève — configurez-les dans
                    Caisse → Configuration → Affectations.
                  </div>
                )}
                {!totalScolariteLoading && affectationsClasse.length > 0 && (
                  <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 2 }}>
                    {affectationsClasse.map(a => (
                      <div key={a.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "var(--m)" }}>
                        <span>{a.tranche_libelle || `Tranche #${a.tranche}`}</span>
                        <span>{Number(a.montant).toLocaleString()} FCFA</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="factions">
              <button className="bsec" onClick={annuler} disabled={saving}>Annuler</button>
              <button className="bprim" onClick={handleSubmit} disabled={saving}>
                {saving
                  ? <><i className="ti ti-loader" style={{ animation: "spin 1s linear infinite" }} /> En cours...</>
                  : <><i className="ti ti-check" /> Inscrire</>}
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        .page-inscription { padding: 8px 4px 40px; }
        .page-header { display: flex; align-items: center; gap: 12px; margin-bottom: 18px; }
        .page-header h2 { font-size: 18px; margin: 0; }
        .ibtn {
          width: 36px; height: 36px; border-radius: 8px; border: 1px solid var(--b);
          background: var(--s2); cursor: pointer; display: flex; align-items: center; justify-content: center;
        }
      `}</style>
    </div>
  );
}